import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * iPhone handoff no-app window (flow G, spec §4.G). 4s, not 1.5s (2026-09-25):
 * iOS first shows a system confirm ("Open in Passwords?") and the page stays
 * `visible` while it is up — the short window fired a false "nothing opened".
 */
export const MFA_HANDOFF_NO_APP_TIMEOUT_MS = 4000;

/**
 * How long to wait, after a `blur` while armed, for a `visibilitychange →
 * hidden` / `pagehide` to confirm a real handoff (2026-09-25 blur fix). A
 * `blur` alone is AMBIGUOUS — it fires for both a genuine app switch and
 * iOS's own "Open in Passwords?" / "Cannot open" system alert, which never
 * hides the page. This is how long we wait to tell the two apart before
 * resuming the no-app countdown.
 */
export const MFA_HANDOFF_BLUR_GRACE_MS = 1500;

/**
 * RD-SEC-015 — iPhone handoff for the "Abrir en mi app" link (flow G).
 *
 * The link is a real `<a href={totp.uri}>` (must be a genuine user gesture
 * for iOS to honor the `otpauth://` scheme). `startHandoff()` ARMS the
 * handoff and starts a {@link MFA_HANDOFF_NO_APP_TIMEOUT_MS} countdown:
 *   - `visibilitychange → hidden` / `pagehide` while armed → an app
 *     genuinely opened: `leftForApp` latches true, every timer is cancelled,
 *     and the handoff attempt ENDS (`armedRef` clears).
 *   - `blur` while armed is AMBIGUOUS (2026-09-25 fix — see the two module
 *     constants above): it fires for a real app switch AND for iOS's own
 *     system confirm, which never hides the page. The countdown is PAUSED
 *     (remaining time kept, never reset) and a
 *     {@link MFA_HANDOFF_BLUR_GRACE_MS} grace window starts:
 *       - a hide *inside* the grace window is a real handoff (see above) —
 *         the note never fires, `leftForApp` latches.
 *       - window `focus` returning FIRST (no hide in between) means the
 *         system prompt was dismissed — the countdown RESUMES from where it
 *         was paused, never restarting a fresh full window.
 *       - neither happening before the grace window itself elapses also
 *         resumes the countdown, so an attempt can never get stuck paused.
 *   - if the countdown reaches zero while still armed (nothing above ended
 *     the attempt first), no app caught the link — `noAppDetected` flips
 *     true (+ `onNoApp()`) for the inline note, and the attempt ENDS.
 *   - returning to `visible` after a confirmed handoff calls `onReturn()`
 *     (the caller advances to the code step with the "you're back" subtitle).
 *
 * `armedRef` is true for the ENTIRE handoff attempt, start to end — that is
 * what makes a LATER, unrelated blur/hide (e.g. backgrounding the app for
 * something else entirely, well after this attempt already resolved) a
 * no-op: it clears only when the attempt itself resolves (timer fires, a
 * real handoff is confirmed via hide, or `startHandoff()` rearms it) — never
 * merely on a `blur`, and never left armed past its own resolution.
 */
export default function useMfaHandoff({ onReturn, onNoApp } = {}) {
  const [leftForApp, setLeftForApp] = useState(false);
  const [noAppDetected, setNoAppDetected] = useState(false);

  const noAppTimerRef = useRef(null);
  const graceTimerRef = useRef(null);
  const deadlineRef = useRef(null);
  const remainingRef = useRef(MFA_HANDOFF_NO_APP_TIMEOUT_MS);
  const armedRef = useRef(false);
  const leftForAppRef = useRef(false);
  // True from a `blur` (while armed) until either a hide confirms a real
  // handoff or a `focus` / the grace timer itself resumes the countdown —
  // the window where we don't yet know which of the two blur causes this is.
  const awaitingResolutionRef = useRef(false);
  const onNoAppRef = useRef(onNoApp);
  onNoAppRef.current = onNoApp;

  const clearNoAppTimer = useCallback(() => {
    if (noAppTimerRef.current != null) {
      clearTimeout(noAppTimerRef.current);
      noAppTimerRef.current = null;
    }
  }, []);

  const clearGraceTimer = useCallback(() => {
    if (graceTimerRef.current != null) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);

  const fireNoApp = useCallback(() => {
    armedRef.current = false;
    awaitingResolutionRef.current = false;
    setNoAppDetected(true);
    onNoAppRef.current?.();
  }, []);

  const startNoAppTimer = useCallback((ms) => {
    clearNoAppTimer();
    remainingRef.current = ms;
    deadlineRef.current = Date.now() + ms;
    noAppTimerRef.current = setTimeout(() => {
      noAppTimerRef.current = null;
      if (armedRef.current && !leftForAppRef.current) fireNoApp();
    }, ms);
  }, [clearNoAppTimer, fireNoApp]);

  const startHandoff = useCallback(() => {
    setNoAppDetected(false);
    leftForAppRef.current = false;
    setLeftForApp(false);
    armedRef.current = true;
    awaitingResolutionRef.current = false;
    clearGraceTimer();
    startNoAppTimer(MFA_HANDOFF_NO_APP_TIMEOUT_MS);
  }, [clearGraceTimer, startNoAppTimer]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return undefined;

    const leave = () => {
      setNoAppDetected(false);
      // Only a hide while a handoff is ARMED counts as "left for the app" —
      // a background/foreground cycle unrelated to the tap must not
      // retroactively arm the return-to-code path.
      if (armedRef.current) {
        armedRef.current = false;
        awaitingResolutionRef.current = false;
        leftForAppRef.current = true;
        setLeftForApp(true);
        clearNoAppTimer();
        clearGraceTimer();
      }
    };

    // The OS confirm / app switch blurs the window FIRST — on its own this
    // can't tell a real handoff apart from iOS's own system alert (see the
    // hook's doc comment). Pause the countdown (keep the remaining time, do
    // NOT disarm) and wait out a short grace window for the hide a real
    // handoff produces.
    const onBlur = () => {
      if (!armedRef.current) return;
      if (noAppTimerRef.current != null) {
        clearNoAppTimer();
        remainingRef.current = Math.max(0, (deadlineRef.current ?? Date.now()) - Date.now());
      }
      awaitingResolutionRef.current = true;
      clearGraceTimer();
      graceTimerRef.current = setTimeout(() => {
        graceTimerRef.current = null;
        if (armedRef.current && awaitingResolutionRef.current) {
          awaitingResolutionRef.current = false;
          startNoAppTimer(remainingRef.current);
        }
      }, MFA_HANDOFF_BLUR_GRACE_MS);
    };

    // Focus returning before any hide means the system prompt was dismissed
    // (cancelled, or "can't open" — no app installed) — resume counting
    // down from where the blur paused it, never a fresh full window.
    const onFocus = () => {
      if (!armedRef.current || !awaitingResolutionRef.current) return;
      awaitingResolutionRef.current = false;
      clearGraceTimer();
      startNoAppTimer(remainingRef.current);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        leave();
        return;
      }
      if (document.visibilityState === 'visible' && leftForAppRef.current) {
        leftForAppRef.current = false;
        setLeftForApp(false);
        onReturn?.();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pagehide', leave);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pagehide', leave);
    };
  }, [clearNoAppTimer, clearGraceTimer, startNoAppTimer, onReturn]);

  useEffect(() => () => {
    clearNoAppTimer();
    clearGraceTimer();
  }, [clearNoAppTimer, clearGraceTimer]);

  return { startHandoff, leftForApp, noAppDetected };
}
